import * as apiFunction from '@services/apiFunctions'

import { clearsEmptyProperties } from '@utils/functions/clearsEmptyProperties'
import { API_URLS } from '@services/apiRoutes'

import type {
  ApprovalWorkflowRequestProps,
  BulkDistributeBillingRequestProps,
  EditDetailedSummaryRequestProps,
  GetClientsRequestProps,
  GetClWeeklyBreakdownRequestProps,
  GetDetailedSummaryRequestProps,
  GetPreviewDetailsRequestProps,
  PreviousInvoicingRequestProps
} from './types'
import type {
  CreateNewOtherServiceRequestProps,
  GetOtherServicesRequestProps,
  RemoveOtherServiceRequestProps
} from './clients/types'

export const reversePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/reverter-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const restorePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/restaurar-previa/dt-competencia/${payload.competenceDate}`,
    method: 'DELETE'
  })
}

export const releasePreviewInvoicing = async (payload: PreviousInvoicingRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/liberar-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const approvalWorkflow = async (payload: ApprovalWorkflowRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/week-status`,
    method: 'PATCH',
    body: {
      billingReferenceDate: payload.competenceDate
    }
  })
}

export const getPreviewDetails = async (payload: GetPreviewDetailsRequestProps) => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/detalhe-previa/dt-competencia/${payload.competenceDate}`,
    method: 'GET'
  })
}

export const editDetailedSummary = async (payload: EditDetailedSummaryRequestProps) => {
  const data = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/resumo-detalhado`,
    method: 'PUT',
    body: data
  })
}

export const getDetailedSummary = async (payload: GetDetailedSummaryRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/resumo-detalhado`,
    method: 'POST',
    body: {
      fatDtCompetencia: payload.competenceDate
    }
  })
}

export const getWeeklyBreakdown = async (payload: GetClWeeklyBreakdownRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/busca-detalhamento-semanal`,
    method: 'POST',
    body: {
      fatDtCompetencia: payload.competenceDate,
      currentPage: payload.currentPage,
      itemsPerPage: payload.itemsPerPage,
      ...(payload.sortBy && { sortBy: payload.sortBy }),
      ...(payload.sortType && { sortType: payload.sortType }),
      ...(payload.serviceGroupingFilter?.length && { serviceGroupingFilter: payload.serviceGroupingFilter }),
      ...(payload.salesItemNameFilter?.length && { salesItemNameFilter: payload.salesItemNameFilter })
    }
  })
}

export const removeOtherService = async ({ serviceId, filiCode }: RemoveOtherServiceRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${filiCode}/outros-servicos/${serviceId}`,
    method: 'DELETE'
  })
}

export const editOtherService = async (payload: CreateNewOtherServiceRequestProps) => {
  const data = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${data.branchCode}/outros-servicos/${data.idOutrosServicos}`,
    method: 'PUT',
    body: data
  })
}

export const createNewOtherService = async (payload: CreateNewOtherServiceRequestProps) => {
  const data = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${data.branchCode}/outros-servicos`,
    method: 'POST',
    body: data
  })
}

export const getOtherServices = async (payload: GetOtherServicesRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/${payload.filiCode}/busca-outros-servicos`,
    method: 'POST',
    body: {
      fatDtCompetencia: payload.competenceDate,
      currentPage: payload.currentPage,
      itemsPerPage: payload.itemsPerPage
    }
  })
}

export const bulkDistributeBilling = async (payload: BulkDistributeBillingRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/distribuir-em-lote`,
    method: 'POST',
    body: payload
  })
}

export const getClients = async (payload: GetClientsRequestProps, searchText?: string) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.invoicing}/clientes/${payload.filiCodigo}`,
    method: 'POST',
    body: {
      fatDtCompetencia: payload.fatDtCompetencia,
      pagina: payload.currentPage,
      limite: payload.itemsPerPage
    },
    params: {
      termo: searchText || null
    }
  })
}
